import type { BetaKey, BetaSource } from './constants';

import { toDaffyPathForCookie, toFrontendDashboardPathForCookie } from './rolloutPaths';

export type NavAction = { type: 'reload' } | { type: 'assign'; url: string };

/** Fallback `/beta/...` path when enabling an area from a page outside its registered set. */
const DEFAULT_BETA_PATH: Partial<Record<BetaKey, string>> = {
  betaRoutes: '/beta/journeys/applications',
  betaInvestigations: '/beta/investigations',
};

/**
 * Post-success navigation strategy, driven by which surface toggled and which key
 * (FRONTEND-655 / FRONTEND-656 / FRONTEND-787):
 *
 * | Toggle                | agentMenu / banner                                                | settings           |
 * | --------------------- | ------------------------------------------------------------------ | ------------------ |
 * | An area (betaRoutes,  | registered → that area's registry path; else → the area's default; | reload current URL |
 * | betaInvestigations)   | current page belongs to a *different* area → stay put (reload)     |                     |
 * | Side nav (fepSidenav) | reload current URL                                                  | reload current URL |
 *
 * Settings never hard-navs to /beta (no /beta Settings page exists), so it always reloads
 * the current legacy URL; the sidenav toggle reloads the current URL on every surface.
 *
 * Disabling an area from a legacy (non-/beta) page is a no-op reload — the user is already on
 * the correct page, so there's nothing to map back to. Disabling one area while sitting on a
 * *different* area's registered page is also a no-op reload -- the page you're on isn't the
 * one being turned off.
 *
 * Path pairs come from David's `routeRolloutRegistry` via local helpers.
 *
 * TODO(FRONTEND-608): Switch the rolloutPaths imports from `./rolloutPaths` to
 * `@usealloy/daffy-to-frontend-dashboard-rollout` once David's FD counterpart exposes those
 * helpers; then delete the local shim.
 */
export function resolveNavAction({
  source,
  key,
  enabled,
  pathname,
}: {
  source: BetaSource;
  key: BetaKey;
  enabled: boolean;
  pathname: string;
}): NavAction {
  // Settings and the sidenav toggle only ever reload the current URL.
  if (source === 'settings' || key === 'fepSidenav') {
    return { type: 'reload' };
  }

  if (enabled) {
    // Registered pages map 1:1. Unregistered pages land on this area's default path — do not
    // `/beta`-prefix the current URL (FE2.0's v3-strip + catch-all 404s those, and BetaGate
    // never gets a chance to bounce).
    return {
      type: 'assign',
      url: toFrontendDashboardPathForCookie(pathname, key) ?? DEFAULT_BETA_PATH[key] ?? pathname,
    };
  }

  const mappedOff = toDaffyPathForCookie(pathname, key);
  if (mappedOff) {
    return { type: 'assign', url: mappedOff };
  }
  // The current page isn't a registered path for the area being disabled (it belongs to a
  // different area, isn't in the registry, or is already a legacy page) — nothing to map back
  // to, so stay put.
  return { type: 'reload' };
}

/** Executes a resolved nav action against the real browser location. */
export function applyNavAction(action: NavAction): void {
  if (action.type === 'reload') {
    window.location.reload();
    return;
  }
  window.location.href = `${window.location.origin}${action.url}`;
}
